import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation } from "@tanstack/react-query";
import { useEffect, useMemo, useState } from "react";
import { useLocation } from "wouter";
import { Loader2, Trash2, Plus, User, Building2 } from "lucide-react";
import { ContactFormModal } from "@/components/contact-form-modal";
import { AccountFormModal } from "@/components/account-form-modal";
import { ConflictResolutionModal } from "@/components/conflict-resolution-modal";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { CurrencyInput } from "@/components/ui/currency-input";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useToast } from "@/hooks/use-toast";
import { insertDealSchema, type Deal, type Contact, type Account, type ClientGroup, type ClientGroupMigrationPreviewResponse, type ClientGroupMigrationExecuteResponse, type MigrationApplicant } from "@shared/schema";
import { apiRequest, queryClient } from "@/lib/queryClient";
import type { z } from "zod";
import { useQuery, useQueries } from "@tanstack/react-query";

// Omit applicantId and applicantType from the schema for the form (using legacy borrowerId/borrowerType columns)
// ISSUE-118: Add conditional validation for settlement dates
// ISSUE-120: Remove unnecessary fields from deal form (probability, amountApproved, commissionStatus, estimatedCommission, expectedSettlementDate)
// Note: 'stage' field was never in the schema - transactionStage, scenarioSubstage, dealSubstage are used instead

// Base schema for type inference (without refine which creates ZodEffects and breaks inference)
const dealFormBaseSchema = insertDealSchema.omit({
  borrowerId: true,
  borrowerType: true,
  probability: true,
  amountApproved: true,
  commissionStatus: true,
  estimatedCommission: true,
  expectedSettlementDate: true,
});

// Schema with validation refinement for actual use
const dealFormSchema = dealFormBaseSchema.refine((data) => {
  // Actual settlement date required when deal is settled
  const isSettled = data.dealSubstage === "settled";
  if (isSettled && !data.actualSettlementDate) {
    return false;
  }
  return true;
}, {
  message: "Actual Settlement Date is required when deal is settled",
  path: ["actualSettlementDate"],
});

// Use base schema for type inference to avoid ZodEffects type issues
type DealFormData = z.infer<typeof dealFormBaseSchema>;

interface DealFormModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  deal?: Deal;
  applicantId?: string;
  applicantType?: "contact" | "account";
  contextClientGroupId?: string; // ISSUE-123: Client group context for filtering and locking
}

interface Applicant {
  id: string; // temporary UI ID
  serverPartyId?: string;
  applicantType: "contact" | "account";
  applicantId: string;
  applicantRole: "primary_applicant" | "co_applicant" | "guarantor";
}

const DEAL_TYPES = [
  { value: "equipment", label: "Equipment Finance" },
  { value: "property", label: "Property Finance" },
  { value: "working_capital", label: "Working Capital" },
  { value: "residential", label: "Residential" },
  { value: "business_acquisition", label: "Business Acquisition" },
  { value: "smsf_finance", label: "SMSF Finance" },
  { value: "development_finance", label: "Development Finance" },
  { value: "other", label: "Other" },
];

const TRANSACTION_STAGES = [
  { value: "scenario", label: "Scenario" },
  { value: "deal", label: "Deal" },
  { value: "funded_loan", label: "Funded Loan" },
];

const SCENARIO_SUBSTAGES = [
  { value: "initial_inquiry", label: "Initial Inquiry" },
  { value: "gathering_info", label: "Gathering Info" },
  { value: "seeking_indicatives", label: "Seeking Indicatives" },
  { value: "preparing_proposal", label: "Preparing Proposal" },
  { value: "awaiting_decision", label: "Awaiting Decision" },
];

const DEAL_SUBSTAGES = [
  { value: "application_prep", label: "Application Prep" },
  { value: "submitted", label: "Submitted" },
  { value: "lender_assessment", label: "Lender Assessment" },
  { value: "conditional_approval", label: "Conditional Approval" },
  { value: "formal_approval", label: "Formal Approval" },
  { value: "settlement_booked", label: "Settlement Booked" },
  { value: "settled", label: "Settled" },
];

const APPLICANT_TYPES = [
  { value: "contact", label: "Contact" },
  { value: "account", label: "Account" },
];

const APPLICANT_ROLES = [
  { value: "primary_applicant", label: "Primary Applicant" },
  { value: "co_applicant", label: "Co-Applicant" },
  { value: "guarantor", label: "Guarantor" },
];

export function DealFormModal({ open, onOpenChange, deal, applicantId, applicantType, contextClientGroupId }: DealFormModalProps) {
  const { toast } = useToast();
  const [, navigate] = useLocation();
  const isEdit = !!deal;
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [showQuickContactModal, setShowQuickContactModal] = useState(false);
  const [showQuickAccountModal, setShowQuickAccountModal] = useState(false);
  const [applicants, setApplicants] = useState<Applicant[]>([]);
  const [isInitialized, setIsInitialized] = useState(false);
  const [showConflictModal, setShowConflictModal] = useState(false);
  const [conflictPreviewData, setConflictPreviewData] = useState<ClientGroupMigrationPreviewResponse | null>(null);
  const [pendingFormData, setPendingFormData] = useState<DealFormData | null>(null);
  const [isCheckingConflicts, setIsCheckingConflicts] = useState(false);
  const [clientGroupFilter, setClientGroupFilter] = useState<string | null>(null);

  // Fetch contacts for applicant lookup (filtered by client group)
  const { data: contacts = [] } = useQuery<Contact[]>({
    queryKey: ["/api/crm/contacts", { ...(clientGroupFilter ? { clientGroupId: clientGroupFilter } : {}) }],
  });

  // F002: Fetch lenders for dropdown
  const { data: lenders = [] } = useQuery<Array<{ id: string; lenderName: string; lenderCode: string }>>({
    queryKey: ["/api/crm/lenders"],
  });

  // Fetch accounts for applicant lookup (filtered by client group)
  const { data: accounts = [] } = useQuery<Account[]>({
    queryKey: ["/api/crm/accounts", { ...(clientGroupFilter ? { clientGroupId: clientGroupFilter } : {}) }],
  });

  // Fetch client groups for filter dropdown
  const { data: clientGroups = [] } = useQuery<ClientGroup[]>({
    queryKey: ["/api/crm/client-groups"],
  });

  // Filter contacts and accounts by selected client group
  const filteredContacts = clientGroupFilter
    ? contacts.filter(c => c.clientGroupId === clientGroupFilter)
    : contacts;

  const filteredAccounts = clientGroupFilter
    ? accounts.filter(a => a.clientGroupId === clientGroupFilter)
    : accounts;

  // Fetch the specific applicant to auto-fill clientGroupId when creating a new deal
  const shouldFetchApplicant = !isEdit && applicantId && applicantType;
  const { data: applicant } = useQuery<Contact | Account>({
    queryKey: applicantType === "contact" 
      ? ["/api/crm/contacts", applicantId] 
      : ["/api/crm/accounts", applicantId],
    enabled: !!shouldFetchApplicant,
  });

  // Fetch existing parties for edit mode (from deal parties table)
  const { data: existingPartiesData } = useQuery<{
    manual: any[];
    facility_propagated: any[];
    association_propagated: any[];
    all: any[];
  }>({
    queryKey: [`/api/crm/deals/${deal?.id}/parties`],
    enabled: isEdit && !!deal?.id,
  });
  const existingApplicants = useMemo(() => (existingPartiesData?.manual ?? []).filter(
    party => (party.status ?? 'active') === 'active' && ['borrower', 'guarantor'].includes(party.role),
  ), [existingPartiesData]);
  const retainedApplicantQueries = useQueries({
    queries: applicants.map(item => ({
      queryKey: [item.applicantType === 'contact' ? '/api/crm/contacts' : '/api/crm/accounts', item.applicantId],
      enabled: open && !!item.applicantId && !(item.applicantType === 'contact' ? filteredContacts : filteredAccounts).some(record => record.id === item.applicantId),
    })),
  });
  const retainedApplicantName = (index: number) => {
    const item = applicants[index];
    if ((item.applicantType === 'contact' ? filteredContacts : filteredAccounts).some(record => record.id === item.applicantId)) return undefined;
    const record = retainedApplicantQueries[index]?.data as Contact | Account | undefined;
    if (record?.id === item.applicantId) {
      return 'legalName' in record ? record.legalName : `${record.firstName || ''} ${record.lastName || ''}`.trim();
    }
    return existingApplicants.find(party => party.partyId === item.applicantId && party.partyType === item.applicantType)?.partyName;
  };

  const form = useForm<DealFormData>({
    resolver: zodResolver(dealFormSchema),
    defaultValues: {
      dealName: "",
      dealType: "equipment",
      transactionStage: "scenario",
      scenarioSubstage: "initial_inquiry",
      dealSubstage: null,
      amountRequested: "",
      lenderId: null, // F002: Sprint 7
      lenderName: "",
      actualSettlementDate: "",
      actualCommission: "",
      clientGroupId: "",
      assignedTo: "",
      notes: "",
    },
  });

  const defaultValues = {
    dealName: "",
    dealType: "equipment" as const,
    transactionStage: "scenario" as const,  // Q9 primary lifecycle stage
    scenarioSubstage: "initial_inquiry" as const,  // Default scenario substage
    amountRequested: null as string | null,
    lenderId: null as string | null, // F002: Sprint 7
    lenderName: null as string | null,
    actualSettlementDate: null as string | null,
    actualCommission: null as string | null,
    clientGroupId: null as string | null,
    assignedTo: null as string | null,
    notes: null as string | null,
    financeDate: null as string | null, // F010: Sprint 7
    lodgedDate: null as string | null,  // F010: Sprint 7
  };

  // Normalize database record for form (numbers -> strings, dates -> string dates)
  const normalizeForForm = (record: Deal): DealFormData => ({
    dealName: record.dealName,
    dealType: record.dealType,
    transactionStage: record.transactionStage,
    scenarioSubstage: record.scenarioSubstage ?? null,
    dealSubstage: record.dealSubstage ?? null,
    amountRequested: record.amountRequested?.toString() ?? null,
    actualCommission: record.actualCommission?.toString() ?? null,
    actualSettlementDate: record.actualSettlementDate ?? null,
    lenderId: record.lenderId ?? null, // F002: Sprint 7
    lenderName: record.lenderName ?? null,
    notes: record.notes ?? null,
    clientGroupId: record.clientGroupId ?? null,
    assignedTo: record.assignedTo ?? null,
    financeDate: record.financeDate ?? null, // F010: Sprint 7
    lodgedDate: record.lodgedDate ?? null,  // F010: Sprint 7
  });

  // Initialize form when modal opens - only reset form once per modal session
  useEffect(() => {
    if (open && !isInitialized) {
      if (deal) {
        // Edit mode: populate form with deal data
        const normalizedDeal = normalizeForForm(deal);
        form.reset(normalizedDeal);
        // In edit mode, default filter to "All Client Groups" to show existing multi-group applicants
        setClientGroupFilter(null);
      } else {
        // Create mode
        const initialValues = {
          ...defaultValues,
        };

        // Auto-fill clientGroupId from applicant if provided (fallback)
        if (applicant?.clientGroupId) {
          initialValues.clientGroupId = applicant.clientGroupId;
        }

        form.reset(initialValues);
      }
      setIsInitialized(true);
    }
  }, [open, deal, applicant, isInitialized]);

  // ISSUE-130: Separate effect for contextClientGroupId to handle it independently
  // This ensures the context is applied even if the modal is already initialized
  useEffect(() => {
    if (open && contextClientGroupId && !deal) {
      // Only apply in create mode (not edit mode)
      form.setValue("clientGroupId", contextClientGroupId);
      setClientGroupFilter(contextClientGroupId);
    }
  }, [open, contextClientGroupId, deal]);

  // Load existing parties in edit mode (runs when existingApplicants data loads)
  // Reverse role mapping: borrower → primary_applicant (first) or co_applicant (subsequent), guarantor → guarantor
  useEffect(() => {
    if (open && deal) {
      let borrowerCount = 0;
      setApplicants(
        existingApplicants.map((party, index) => {
          let applicantRole: "primary_applicant" | "co_applicant" | "guarantor";
          if (party.role === 'guarantor') {
            applicantRole = 'guarantor';
          } else {
            applicantRole = borrowerCount === 0 ? 'primary_applicant' : 'co_applicant';
            borrowerCount++;
          }
          return {
            id: `existing-${index}`,
            serverPartyId: party.id,
            applicantType: (party.partyType ?? party.applicantType) as "contact" | "account",
            applicantId: party.partyId ?? party.applicantId,
            applicantRole,
          };
        })
      );
    }
  }, [open, deal, existingApplicants]);

  // Pre-fill applicants in create mode if applicantId/applicantType provided
  useEffect(() => {
    if (open && !deal && applicantId && applicantType) {
      setApplicants([
        {
          id: `prefill-${Date.now()}`,
          applicantType: applicantType,
          applicantId: applicantId,
          applicantRole: "primary_applicant",
        },
      ]);
    } else if (open && !deal && !applicantId) {
      setApplicants([]);
    }
  }, [open, deal, applicantId, applicantType]);

  const handleOpenChange = (newOpen: boolean) => {
    if (!newOpen) {
      form.reset(defaultValues);
      setApplicants([]);
      setIsInitialized(false);
      setClientGroupFilter(null);
    }
    onOpenChange(newOpen);
  };

  // Applicant management functions
  const handleAddApplicant = () => {
    const newApplicant: Applicant = {
      id: `new-${Date.now()}`,
      applicantType: "contact",
      applicantId: "",
      applicantRole: hasPrimaryApplicant() ? "co_applicant" : "primary_applicant",
    };
    setApplicants([...applicants, newApplicant]);
  };

  // F005: Check for duplicate applicant before adding
  const isDuplicateApplicant = (applicantId: string): boolean => {
    return applicants.some(app => app.applicantId === applicantId);
  };

  const handleRemoveApplicant = (index: number) => {
    const applicant = applicants[index];
    
    // Prevent removing the last primary applicant
    if (applicant.applicantRole === "primary_applicant") {
      const primaryCount = applicants.filter(a => a.applicantRole === "primary_applicant").length;
      if (primaryCount === 1) {
        toast({
          title: "Cannot Remove",
          description: "At least one primary applicant is required",
          variant: "destructive",
        });
        return;
      }
    }

    setApplicants(applicants.filter((_, i) => i !== index));
  };

  const updateApplicant = (index: number, field: keyof Applicant, value: any) => {
    const updated = [...applicants];
    updated[index] = { ...updated[index], [field]: value };

    // If changing to primary_applicant, ensure only one primary
    if (field === "applicantRole" && value === "primary_applicant") {
      updated.forEach((app, i) => {
        if (i !== index && app.applicantRole === "primary_applicant") {
          updated[i] = { ...app, applicantRole: "co_applicant" };
        }
      });
    }

    setApplicants(updated);
  };

  // Atomic update for applicant type changes (ISSUE-125 fix)
  // Updates both applicantType and applicantId in a single state operation
  // to prevent React state batching issues that cause the type to revert
  const updateApplicantType = (index: number, newType: "contact" | "account") => {
    const updated = [...applicants];
    updated[index] = {
      ...updated[index],
      applicantType: newType,
      applicantId: "" // Reset selection when type changes
    };
    setApplicants(updated);
  };

  const hasPrimaryApplicant = () => {
    return applicants.some(a => a.applicantRole === "primary_applicant");
  };

  const isSubmitDisabled = () => {
    return applicants.length === 0 || !hasPrimaryApplicant();
  };



  const createMutation = useMutation({
    mutationFn: async (data: DealFormData) => {
      // First create the deal
      const dealRes = await apiRequest("POST", "/api/crm/deals", {
        ...data,
        // Set dummy values for borrowerId/borrowerType as they're required in schema
        borrowerId: applicants[0]?.applicantId || "",
        borrowerType: applicants[0]?.applicantType || "contact",
      });
      
      const dealData = await dealRes.json();
      const dealId = dealData.id;

      // Create parties via the deal parties endpoint (replaces legacy applicants endpoint)
      // Role mapping: primary_applicant/co_applicant → borrower, guarantor → guarantor
      const roleMap: Record<string, string> = {
        primary_applicant: 'borrower',
        co_applicant: 'borrower',
        guarantor: 'guarantor',
      };
      await Promise.all(
        applicants.map(applicant =>
          apiRequest("POST", `/api/crm/deals/${dealId}/parties`, {
            partyId: applicant.applicantId,
            partyType: applicant.applicantType,
            role: roleMap[applicant.applicantRole] ?? 'borrower',
          })
        )
      );

      return dealData;
    },
    onSuccess: (dealData) => {
      // Refetch all deal-related queries (including filtered queries like ?applicantId=...)
      queryClient.refetchQueries({
        predicate: (query) => {
          const key = query.queryKey[0];
          return typeof key === 'string' && key.startsWith('/api/crm/deals');
        }
      });
      toast({
        title: "Success",
        description: "Deal created successfully",
      });
      handleOpenChange(false);

      // Auto-navigate to deal detail page
      navigate(`/deals/${dealData.id}`);
    },
    onError: (error: Error) => {
      toast({
        title: "Error",
        description: error.message || "Failed to create deal",
        variant: "destructive",
      });
    },
  });

  const updateMutation = useMutation({
    mutationFn: async (data: DealFormData) => {
      return apiRequest("PATCH", `/api/crm/deals/${deal?.id}`, {
        ...data,
        expectedManualParties: existingApplicants.map(party => ({
          id: party.id, partyId: party.partyId, partyType: party.partyType, role: party.role,
        })),
        manualParties: applicants.map(applicant => ({
          ...(applicant.serverPartyId ? { id: applicant.serverPartyId } : {}),
          partyId: applicant.applicantId,
          partyType: applicant.applicantType,
          role: applicant.applicantRole === 'guarantor' ? 'guarantor' : 'borrower',
        })),
      });
    },
    onSuccess: () => {
      // Refetch all deal-related queries (including filtered queries like ?applicantId=...)
      queryClient.refetchQueries({
        predicate: (query) => {
          const key = query.queryKey[0];
          return typeof key === 'string' && key.startsWith('/api/crm/deals');
        }
      });
      toast({
        title: "Success",
        description: "Deal updated successfully",
      });
      handleOpenChange(false);
    },
    onError: (error: Error) => {
      toast({
        title: "Error",
        description: error.message || "Failed to update deal",
        variant: "destructive",
      });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: () =>
      apiRequest("DELETE", `/api/crm/deals/${deal?.id}`),
    onSuccess: () => {
      // Refetch all deal-related queries (including filtered queries like ?applicantId=...)
      queryClient.refetchQueries({
        predicate: (query) => {
          const key = query.queryKey[0];
          return typeof key === 'string' && key.startsWith('/api/crm/deals');
        }
      });
      toast({
        title: "Success",
        description: "Deal deleted successfully",
      });
      handleOpenChange(false);
    },
    onError: (error: Error) => {
      toast({
        title: "Error",
        description: error.message || "Failed to delete deal",
        variant: "destructive",
      });
    },
  });

  const checkForClientGroupConflicts = async (data: DealFormData): Promise<boolean> => {
    const migrationApplicants: MigrationApplicant[] = applicants.map(a => ({
      applicantId: a.applicantId,
      applicantType: a.applicantType,
      applicantRole: a.applicantRole,
    }));

    setIsCheckingConflicts(true);
    try {
      const response = await apiRequest("POST", "/api/crm/client-group-migrations/preview", {
        applicants: migrationApplicants,
      });
      const preview: ClientGroupMigrationPreviewResponse = await response.json();

      if (preview.hasConflict) {
        setConflictPreviewData(preview);
        setPendingFormData(data);
        setShowConflictModal(true);
        return true;
      }
      return false;
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message || "Failed to check for client group conflicts",
        variant: "destructive",
      });
      return false;
    } finally {
      setIsCheckingConflicts(false);
    }
  };

  const proceedWithDealCreation = (data: DealFormData) => {
    if (isEdit) {
      updateMutation.mutate(data);
    } else {
      createMutation.mutate(data);
    }
  };

  const handleConflictResolved = (result: ClientGroupMigrationExecuteResponse) => {
    setShowConflictModal(false);
    if (pendingFormData) {
      const updatedFormData = {
        ...pendingFormData,
        clientGroupId: result.masterClientGroupId,
      };
      form.setValue("clientGroupId", result.masterClientGroupId);
      proceedWithDealCreation(updatedFormData);
    }
    setPendingFormData(null);
    setConflictPreviewData(null);
  };

  const handleConflictCancelled = () => {
    setShowConflictModal(false);
    setPendingFormData(null);
    setConflictPreviewData(null);
  };

  const onSubmit = async (data: DealFormData) => {
    // Validate applicants before submission
    if (applicants.length === 0) {
      toast({
        title: "Validation Error",
        description: "Please add at least one applicant",
        variant: "destructive",
      });
      return;
    }

    if (!hasPrimaryApplicant()) {
      toast({
        title: "Validation Error",
        description: "Please designate at least one primary applicant",
        variant: "destructive",
      });
      return;
    }

    // Check that all applicants have an applicantId selected
    const invalidApplicant = applicants.find(a => !a.applicantId);
    if (invalidApplicant) {
      toast({
        title: "Validation Error",
        description: "Please select a name for all applicants",
        variant: "destructive",
      });
      return;
    }

    // For new deals with multiple applicants, check for client group conflicts
    if (!isEdit && applicants.length > 1) {
      const hasConflict = await checkForClientGroupConflicts(data);
      if (hasConflict) {
        return;
      }
    }

    proceedWithDealCreation(data);
  };

  const handleDelete = () => {
    deleteMutation.mutate();
    setShowDeleteDialog(false);
  };

  const isPending = createMutation.isPending || updateMutation.isPending || deleteMutation.isPending || isCheckingConflicts;

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent
        className="max-w-3xl max-h-[90vh] overflow-y-auto"
        data-testid="dialog-deal-form"
      >
        <DialogHeader>
          <DialogTitle>{isEdit ? "Edit Deal" : "Add Deal"}</DialogTitle>
          <DialogDescription>
            {isEdit
              ? "Update the deal information below"
              : "Enter the details for the new deal"}
          </DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <div className="grid gap-4 md:grid-cols-2">
              <FormField
                control={form.control}
                name="dealName"
                render={({ field }) => (
                  <FormItem className="md:col-span-2">
                    <FormLabel>Deal Name *</FormLabel>
                    <FormControl>
                      <Input
                        placeholder="e.g., ABC Corp Equipment Finance"
                        data-testid="input-deal-name"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="transactionStage"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Transaction Stage *</FormLabel>
                    <Select
                      onValueChange={(value) => {
                        field.onChange(value);
                        // Auto-set appropriate substage when transaction stage changes
                        if (value === "scenario") {
                          form.setValue("scenarioSubstage", "initial_inquiry");
                          form.setValue("dealSubstage", null);
                        } else if (value === "deal") {
                          form.setValue("dealSubstage", "application_prep");
                          form.setValue("scenarioSubstage", null);
                        } else if (value === "funded_loan") {
                          form.setValue("scenarioSubstage", null);
                          form.setValue("dealSubstage", null);
                        }
                      }}
                      defaultValue={field.value}
                      value={field.value}
                    >
                      <FormControl>
                        <SelectTrigger data-testid="select-transaction-stage">
                          <SelectValue placeholder="Select transaction stage" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {TRANSACTION_STAGES.map((stage) => (
                          <SelectItem key={stage.value} value={stage.value}>
                            {stage.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {/* Scenario Substage (conditional - only shown when transaction_stage = 'scenario') */}
              {form.watch("transactionStage") === "scenario" && (
                <FormField
                  control={form.control}
                  name="scenarioSubstage"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Scenario Substage *</FormLabel>
                      <Select
                        onValueChange={field.onChange}
                        defaultValue={field.value || ""}
                        value={field.value || ""}
                      >
                        <FormControl>
                          <SelectTrigger data-testid="select-scenario-substage">
                            <SelectValue placeholder="Select scenario substage" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {SCENARIO_SUBSTAGES.map((substage) => (
                            <SelectItem key={substage.value} value={substage.value}>
                              {substage.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              )}

              {/* Deal Substage (conditional - only shown when transaction_stage = 'deal') */}
              {form.watch("transactionStage") === "deal" && (
                <FormField
                  control={form.control}
                  name="dealSubstage"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Deal Substage *</FormLabel>
                      <Select
                        onValueChange={field.onChange}
                        defaultValue={field.value || ""}
                        value={field.value || ""}
                      >
                        <FormControl>
                          <SelectTrigger data-testid="select-deal-substage">
                            <SelectValue placeholder="Select deal substage" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {DEAL_SUBSTAGES.map((substage) => (
                            <SelectItem key={substage.value} value={substage.value}>
                              {substage.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              )}

              <FormField
                control={form.control}
                name="dealType"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Deal Type *</FormLabel>
                    <Select
                      onValueChange={field.onChange}
                      defaultValue={field.value}
                      value={field.value}
                    >
                      <FormControl>
                        <SelectTrigger data-testid="select-deal-type">
                          <SelectValue placeholder="Select deal type" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {DEAL_TYPES.map((type) => (
                          <SelectItem key={type.value} value={type.value}>
                            {type.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="amountRequested"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Amount Requested</FormLabel>
                    <FormControl>
                      <CurrencyInput
                        placeholder="0.00"
                        data-testid="input-amount-requested"
                        value={field.value ?? undefined}
                        onValueChange={field.onChange}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {/* F002: Lender Dropdown (Sprint 7) */}
              <FormField
                control={form.control}
                name="lenderId"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Lender</FormLabel>
                    <Select
                      onValueChange={(value) => {
                        field.onChange(value === "__none__" ? null : value);
                        // Auto-populate lenderName from selected lender
                        if (value !== "__none__") {
                          const selectedLender = lenders.find((l) => l.id === value);
                          if (selectedLender) {
                            form.setValue("lenderName", selectedLender.lenderName);
                          }
                        } else {
                          form.setValue("lenderName", null);
                        }
                      }}
                      value={field.value ?? "__none__"}
                    >
                      <FormControl>
                        <SelectTrigger data-testid="select-lender">
                          <SelectValue placeholder="Select lender" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        <SelectItem value="__none__">None</SelectItem>
                        {lenders.map((lender) => (
                          <SelectItem key={lender.id} value={lender.id}>
                            {lender.lenderName} ({lender.lenderCode})
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormDescription>
                      Select a lender from the list or leave as None
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {/* Client Group Filter */}
              <div className="md:col-span-2">
                <FormItem>
                  <FormLabel>Filter by Client Group</FormLabel>
                  <Select
                    value={clientGroupFilter ?? "all"}
                    onValueChange={(value) => {
                      const newFilter = value === "all" ? null : value;

                      // If changing filter with existing applicants, clear them and show toast
                      if (applicants.length > 0 && clientGroupFilter !== newFilter) {
                        setApplicants([]);
                        toast({
                          title: "Applicants Cleared",
                          description: "Changing the client group filter has cleared all applicants. Please re-add applicants from the selected group.",
                        });
                      }

                      setClientGroupFilter(newFilter);
                    }}
                    disabled={!!contextClientGroupId} // ISSUE-123: Disable when opened from client group context
                  >
                    <SelectTrigger data-testid="select-client-group-filter">
                      <SelectValue placeholder="All Client Groups" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Client Groups</SelectItem>
                      {clientGroups.map((group) => (
                        <SelectItem key={group.id} value={group.id}>
                          {group.groupName}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormDescription>
                    {contextClientGroupId
                      ? "Client group inherited from context (locked)"
                      : "Filter applicants by client group to narrow down your search"}
                  </FormDescription>
                </FormItem>
              </div>

              {/* Applicants Section - F005: Reordered buttons */}
              <div className="md:col-span-2 space-y-3" data-testid="applicants-section">
                <FormLabel className="text-base">Applicants *</FormLabel>

                {/* F005: Quick-create buttons at TOP with icons */}
                <div className="flex gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => setShowQuickContactModal(true)}
                    data-testid="button-quick-add-contact"
                  >
                    <User className="mr-2 h-4 w-4" />
                    Quick-create Contact
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => setShowQuickAccountModal(true)}
                    data-testid="button-quick-add-account"
                  >
                    <Building2 className="mr-2 h-4 w-4" />
                    Quick-create Account
                  </Button>
                </div>

                {applicants.length === 0 ? (
                  <p className="text-sm text-muted-foreground">
                    No applicants added yet. Click "Add Applicant" to add one.
                  </p>
                ) : (
                  <div className="border rounded-md">
                    <Table>
                      <TableHeader>
                        <TableRow className="h-8 bg-muted/50">
                          <TableHead className="text-xs font-medium px-2 py-0.5 w-[120px]">Type</TableHead>
                          <TableHead className="text-xs font-medium px-2 py-0.5">Name</TableHead>
                          <TableHead className="text-xs font-medium px-2 py-0.5 w-[160px]">Role</TableHead>
                          <TableHead className="text-xs font-medium px-2 py-0.5 w-[60px]">Actions</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {applicants.map((applicant, index) => (
                          <TableRow
                            key={applicant.id}
                            className="h-10"
                            data-testid={`applicant-row-${index}`}
                          >
                            <TableCell className="px-2 py-0.5">
                              <Select
                                value={applicant.applicantType}
                                onValueChange={(value) => {
                                  updateApplicantType(index, value as "contact" | "account");
                                }}
                              >
                                <SelectTrigger
                                  className="h-8 text-xs"
                                  data-testid={`select-applicant-type-${index}`}
                                >
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  {APPLICANT_TYPES.map((type) => (
                                    <SelectItem key={type.value} value={type.value}>
                                      {type.label}
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            </TableCell>

                            <TableCell className="px-2 py-0.5">
                              <Select
                                value={applicant.applicantId}
                                onValueChange={(value) => {
                                  // F005: Check for duplicate applicant
                                  if (isDuplicateApplicant(value)) {
                                    toast({
                                      title: "Duplicate Applicant",
                                      description: "This contact is already an applicant",
                                      variant: "destructive",
                                    });
                                    return; // Don't update if duplicate
                                  }

                                  updateApplicant(index, "applicantId", value);

                                  // Auto-set clientGroupId from selected applicant
                                  if (applicant.applicantType === "contact") {
                                    const contact = contacts.find(c => c.id === value);
                                    if (contact?.clientGroupId && !form.getValues("clientGroupId")) {
                                      form.setValue("clientGroupId", contact.clientGroupId);
                                    }
                                  } else {
                                    const account = accounts.find(a => a.id === value);
                                    if (account?.clientGroupId && !form.getValues("clientGroupId")) {
                                      form.setValue("clientGroupId", account.clientGroupId);
                                    }
                                  }
                                }}
                              >
                                <SelectTrigger
                                  className="h-8 text-xs"
                                  data-testid={`select-applicant-name-${index}`}
                                >
                                  <SelectValue placeholder={`Select ${applicant.applicantType}`}>{retainedApplicantName(index)}</SelectValue>
                                </SelectTrigger>
                                <SelectContent>
                                  {retainedApplicantName(index) && (
                                    <SelectItem value={applicant.applicantId} disabled>
                                      {retainedApplicantName(index)} (Current selection)
                                    </SelectItem>
                                  )}
                                  {applicant.applicantType === "contact" ? (
                                    filteredContacts.length > 0 ? (
                                      filteredContacts.map((contact) => (
                                        <SelectItem key={contact.id} value={contact.id}>
                                          {contact.firstName} {contact.lastName} {contact.email ? `(${contact.email})` : ""}
                                        </SelectItem>
                                      ))
                                    ) : (
                                      <SelectItem value="no-contacts" disabled>
                                        {clientGroupFilter
                                          ? "No contacts in selected client group"
                                          : "No contacts available"}
                                      </SelectItem>
                                    )
                                  ) : (
                                    filteredAccounts.length > 0 ? (
                                      filteredAccounts.map((account) => (
                                        <SelectItem key={account.id} value={account.id}>
                                          {account.legalName} ({account.entityType})
                                        </SelectItem>
                                      ))
                                    ) : (
                                      <SelectItem value="no-accounts" disabled>
                                        {clientGroupFilter
                                          ? "No accounts in selected client group"
                                          : "No accounts available"}
                                      </SelectItem>
                                    )
                                  )}
                                </SelectContent>
                              </Select>
                            </TableCell>

                            <TableCell className="px-2 py-0.5">
                              <Select
                                value={applicant.applicantRole}
                                onValueChange={(value) =>
                                  updateApplicant(index, "applicantRole", value)
                                }
                              >
                                <SelectTrigger
                                  className="h-8 text-xs"
                                  data-testid={`select-applicant-role-${index}`}
                                >
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  {APPLICANT_ROLES.map((role) => (
                                    <SelectItem key={role.value} value={role.value}>
                                      {role.label}
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            </TableCell>

                            <TableCell className="px-2 py-0.5">
                              <Button
                                type="button"
                                variant="ghost"
                                size="icon"
                                className="h-8 w-8"
                                onClick={() => handleRemoveApplicant(index)}
                                data-testid={`button-remove-applicant-${index}`}
                              >
                                <Trash2 className="h-4 w-4 text-destructive" />
                              </Button>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                )}

                {/* F005: Add Applicant button at BOTTOM */}
                <div className="flex justify-end">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={handleAddApplicant}
                    data-testid="button-add-applicant"
                  >
                    <Plus className="mr-2 h-3 w-3" />
                    Add Applicant
                  </Button>
                </div>
              </div>

              {isEdit && (
                <FormField
                  control={form.control}
                  name="actualSettlementDate"
                  render={({ field }) => {
                    // ISSUE-118: Conditional requirement for actual settlement date
                    const dealSubstage = form.watch("dealSubstage");
                    const isSettled = dealSubstage === "settled";

                    return (
                      <FormItem className={isSettled ? "border-2 border-primary rounded-lg p-3" : ""}>
                        <FormLabel className={isSettled ? "font-semibold" : ""}>
                          Actual Settlement Date {isSettled && <span className="text-destructive">*</span>}
                        </FormLabel>
                        {isSettled && (
                          <p className="text-xs text-muted-foreground mb-2">
                            Required when deal is settled.
                          </p>
                        )}
                        <FormControl>
                          <Input
                            type="date"
                            data-testid="input-actual-settlement"
                            {...field}
                            value={field.value ?? ""}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    );
                  }}
                />
              )}

              {isEdit && (
                <FormField
                  control={form.control}
                  name="actualCommission"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Actual Commission</FormLabel>
                      <FormControl>
                        <CurrencyInput
                          placeholder="0.00"
                          data-testid="input-actual-commission"
                          value={field.value ?? undefined}
                          onValueChange={field.onChange}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              )}

              <FormField
                control={form.control}
                name="notes"
                render={({ field }) => (
                  <FormItem className="md:col-span-2">
                    <FormLabel>Notes</FormLabel>
                    <FormControl>
                      <Textarea
                        placeholder="Additional notes or comments..."
                        className="min-h-[100px]"
                        data-testid="textarea-notes"
                        {...field}
                        value={field.value ?? ""}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <DialogFooter className="flex justify-between">
              <div>
                {isEdit && (
                  <Button
                    type="button"
                    variant="destructive"
                    onClick={() => setShowDeleteDialog(true)}
                    disabled={isPending}
                    data-testid="button-delete"
                  >
                    <Trash2 className="mr-2 h-4 w-4" />
                    Delete
                  </Button>
                )}
              </div>
              <div className="flex gap-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => handleOpenChange(false)}
                  disabled={isPending}
                  data-testid="button-cancel"
                >
                  Cancel
                </Button>
                <Button 
                  type="submit" 
                  disabled={isPending || isSubmitDisabled()} 
                  data-testid="button-save"
                >
                  {isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                  {isEdit ? "Update Deal" : "Create Deal"}
                </Button>
              </div>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>

      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Deal</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this deal? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="button-cancel-delete">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              data-testid="button-confirm-delete"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <ContactFormModal
        open={showQuickContactModal}
        onOpenChange={setShowQuickContactModal}
        onContactCreated={(newContact) => {
          // Auto-add the newly created contact to applicants list
          const newApplicant: Applicant = {
            id: `new-${Date.now()}`,
            applicantType: "contact",
            applicantId: newContact.id,
            applicantRole: hasPrimaryApplicant() ? "co_applicant" : "primary_applicant",
          };
          setApplicants([...applicants, newApplicant]);

          // Auto-set clientGroupId if not already set
          if (newContact.clientGroupId && !form.getValues("clientGroupId")) {
            form.setValue("clientGroupId", newContact.clientGroupId);
          }
        }}
        defaultClientGroupId={clientGroupFilter || form.watch("clientGroupId") || undefined}
      />

      <AccountFormModal
        open={showQuickAccountModal}
        onOpenChange={setShowQuickAccountModal}
        onAccountCreated={(newAccount) => {
          // Auto-add the newly created account to applicants list
          const newApplicant: Applicant = {
            id: `new-${Date.now()}`,
            applicantType: "account",
            applicantId: newAccount.id,
            applicantRole: hasPrimaryApplicant() ? "co_applicant" : "primary_applicant",
          };
          setApplicants([...applicants, newApplicant]);

          // Auto-set clientGroupId if not already set
          if (newAccount.clientGroupId && !form.getValues("clientGroupId")) {
            form.setValue("clientGroupId", newAccount.clientGroupId);
          }
        }}
        defaultClientGroupId={clientGroupFilter || form.watch("clientGroupId") || undefined}
      />

      {conflictPreviewData && (
        <ConflictResolutionModal
          open={showConflictModal}
          onOpenChange={setShowConflictModal}
          previewData={conflictPreviewData}
          onResolved={handleConflictResolved}
          onCancel={handleConflictCancelled}
        />
      )}
    </Dialog>
  );
}
